// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ExistingAsset } from '@/api/types';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormTextField } from '@/components/form-text-field';
import { OpenInNewRounded } from '@/constants/hardfin-standard-icons';
import { useSupportError } from '@/hooks/use-support-error';
import { QuarantinedSerial, Shipment } from '@/shipment/api/types';
import { QuarantineResolutionDialogShell } from '@/shipment/components/quarantine-resolution-dialog-shell';
import { QuarantineResolutionPreviewRow } from '@/shipment/components/quarantine-resolution-preview-row';
import { useResolveQuarantinedSerial } from '@/shipment/hooks/use-resolve-quarantined-serial';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Divider, Link, Stack, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

// The ways a duplicate is answered.
type DuplicateResolutionKind = 'CORRECTED_SERIAL' | 'CREATED_ASSET';

function buildFormSchema(isSerialEditable: boolean) {
  return z.object({
    note: z.string().min(1, 'A note is required to resolve a held serial'),
    correctedSerial: isSerialEditable
      ? z.string().trim().min(1, 'A corrected serial is required')
      : z.string(),
  });
}

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

interface Props {
  isOpen: boolean;
  shipmentKey: string;
  quarantinedSerial: QuarantinedSerial;
  kind: DuplicateResolutionKind;
  // Where a created unit comes into being.
  creationLocation: Shipment['fromLocation'];
  inventoryDate: DateTime | null;
  onClose: () => void;
  // Offered when the attempt is refused: swaps in the correction dialog.
  onCorrectSerialInstead: () => void;
}

interface Copy {
  title: string;
  reason: string;
  placeholder: string;
  submitLabel: string;
}

/** The copy for one duplicate resolution, written per hold type. */
function toDialogCopy(
  quarantineType: QuarantinedSerial['quarantineType'],
  kind: DuplicateResolutionKind
): Copy {
  // Why the serial is held; the action says what happens next.
  const reason =
    quarantineType === 'DUPLICATE_IN_ORDER'
      ? 'The 3PL reported this serial more than once on this shipment.'
      : 'Hardfin already has an asset under this serial.';

  if (kind === 'CREATED_ASSET') {
    return {
      title: 'Accept and create',
      reason,
      placeholder: 'Why is this the unit that carries the reported serial?',
      submitLabel: 'Accept and create',
    };
  }
  return {
    title: 'Specify alternate serial',
    reason,
    placeholder: 'Where does the alternate serial come from?',
    submitLabel: 'Specify alternate serial',
  };
}

/** Resolution dialog for a serial held as a duplicate. */
export function QuarantineDuplicateSerialDialog(props: Props) {
  const { showSupportError, hideSupportError } = useSupportError();
  const { user } = useCurrentUser();
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  // The asset holding the serial, so a refusal can point at it.
  const [takenAsset, setTakenAsset] = useState<ExistingAsset | null>(null);
  const resolveMutation = useResolveQuarantinedSerial(props.shipmentKey);

  const isSerialEditable = props.kind === 'CORRECTED_SERIAL';
  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(buildFormSchema(isSerialEditable)),
    // A correction starts blank; this unit does not carry the serial.
    defaultValues: { note: '', correctedSerial: '' },
  });

  const hold = props.quarantinedSerial;
  const reportedItem = hold.reportedItem;
  const copy = toDialogCopy(hold.quarantineType, props.kind);

  // Correcting is only a way out when this dialog is not already it.
  const isCorrectionOffered = !isSerialEditable;
  const recoveryPrefix =
    takenAsset !== null || isCorrectionOffered ? 'You can ' : '';

  const close = (options?: { ignorePending?: boolean }) => {
    if (resolveMutation.isPending && !options?.ignorePending) {
      return;
    }
    props.onClose();
    reset();
    setRefusalMessage(null);
    setTakenAsset(null);
  };

  // The parent swaps dialogs, so clear the refused attempt first.
  const handOffToCorrection = () => {
    reset();
    setRefusalMessage(null);
    setTakenAsset(null);
    props.onCorrectSerialInstead();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    hideSupportError();
    setRefusalMessage(null);
    setTakenAsset(null);
    resolveMutation.mutate(
      {
        quarantineKey: hold.key,
        kind: props.kind,
        note: data.note.trim(),
        correctedSerial: isSerialEditable
          ? data.correctedSerial.trim()
          : undefined,
      },
      {
        onSuccess: () => close({ ignorePending: true }),
        onError: (error) => {
          const apiError = handleAPIError(error);
          // A refusal is the shipment's own state, not a fault.
          const curated = getUserFacingErrorMessage(apiError);
          if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
            hideSupportError();
            setTakenAsset(
              apiError.errorType === 'quarantine_serial_exists'
                ? (apiError.existingAsset ?? null)
                : null
            );
            setRefusalMessage(curated);
            return;
          }
          setRefusalMessage(null);
          if (!apiError.sentToSentry) {
            captureException(error);
          }
          showSupportError(
            'An error occurred while resolving the held serial.'
          );
        },
      }
    );
  };

  return (
    <QuarantineResolutionDialogShell
      isOpen={props.isOpen}
      title={copy.title}
      submitLabel={copy.submitLabel}
      isPending={formState.isSubmitting || resolveMutation.isPending}
      onSubmit={handleSubmit(handleValidSubmission)}
      onClose={() => close()}
    >
      <Typography variant="body1" sx={{ mt: 2 }}>
        The 3PL reported serial <strong>{hold.serial}</strong> as item{' '}
        <strong>{reportedItem.sku}</strong> ({reportedItem.name}). {copy.reason}
      </Typography>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Asset to be created
      </Typography>
      <Stack
        useFlexGap
        spacing={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        <QuarantineResolutionPreviewRow label="Item">
          {`${reportedItem.sku} (${reportedItem.name})`}
        </QuarantineResolutionPreviewRow>
        <QuarantineResolutionPreviewRow label="Initial location">
          {props.creationLocation?.name ?? '—'}
        </QuarantineResolutionPreviewRow>
        <QuarantineResolutionPreviewRow label="Inventory date">
          {
            // The backend dates the asset in the organization's timezone.
            props.inventoryDate
              ?.setZone(user.organization.timezone)
              .toLocaleString({ dateStyle: 'medium' }) ?? '—'
          }
        </QuarantineResolutionPreviewRow>
        {isSerialEditable && <Divider sx={{ my: 1 }} />}
        {isSerialEditable ? (
          <QuarantineResolutionPreviewRow label="Serial">
            <FormTextField
              control={control}
              name="correctedSerial"
              placeholder="Alternate serial for this unit"
            />
          </QuarantineResolutionPreviewRow>
        ) : (
          <QuarantineResolutionPreviewRow label="Serial">
            {hold.serial}
          </QuarantineResolutionPreviewRow>
        )}
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Note
      </Typography>
      <FormTextField
        control={control}
        name="note"
        fullWidth={true}
        isMultiline={true}
        minimumRows={2}
        placeholder={copy.placeholder}
      />

      {refusalMessage !== null && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {refusalMessage} {recoveryPrefix}
          {takenAsset !== null && (
            <Link
              // A new tab loads the page fresh, needing no navigation.
              href={`/assets/${takenAsset.key}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                verticalAlign: 'baseline',
              }}
            >
              open the existing asset
              <OpenInNewRounded fontSize="inherit" />
            </Link>
          )}
          {takenAsset !== null && isCorrectionOffered && ' or '}
          {isCorrectionOffered && (
            <Link
              component="button"
              type="button"
              onClick={handOffToCorrection}
              // Keeps the link on the sentence's baseline.
              sx={{ font: 'inherit', verticalAlign: 'baseline' }}
            >
              specify a new serial
            </Link>
          )}
          {recoveryPrefix !== '' && '.'}
        </Alert>
      )}
    </QuarantineResolutionDialogShell>
  );
}
